/**
 * The dashboard's arithmetic, kept apart from the screen so it can be
 * tested on its own: which months the chart covers, when a VAT return is
 * due, what needs attention, and how a figure is shortened for an axis.
 *
 * Money stays in strings (vouchers/money.ts) wherever it is added up;
 * Number() is used only to size a bar or sort a list, never for a figure
 * that is shown.
 */
import { BUCKETS, type LedgerBalance, type OutstandingReport } from '../api/types'
import { isoDate } from '../dates'
import { addMoney, isPositive, signed, subMoney } from '../vouchers/money'

export interface Month { from: string; to: string; label: string }

/** The last `n` calendar months, oldest first, the current one to today. */
export function lastMonths(n: number, lang: string, now = new Date()): Month[] {
  const fmt = new Intl.DateTimeFormat(lang === 'ar' ? 'ar' : 'en-GB', { month: 'short' })
  const out: Month[] = []
  for (let back = n - 1; back >= 0; back--) {
    const first = new Date(now.getFullYear(), now.getMonth() - back, 1)
    const last = back === 0 ? now : new Date(first.getFullYear(), first.getMonth() + 1, 0)
    out.push({ from: isoDate(first), to: isoDate(last), label: fmt.format(first) })
  }
  return out
}

export interface Quarter { year: number; quarter: number }

export function quarterOf(d: Date): Quarter {
  return { year: d.getFullYear(), quarter: Math.floor(d.getMonth() / 3) + 1 }
}

export function previousQuarter(q: Quarter): Quarter {
  return q.quarter === 1 ? { year: q.year - 1, quarter: 4 }
                         : { year: q.year, quarter: q.quarter - 1 }
}

/**
 * A quarterly KSA VAT return is due by the last day of the month after the
 * quarter ends: Q3 (Jul-Sep) by 31 October.
 */
export function vatDueDate(q: Quarter): string {
  const firstMonthAfter = q.quarter * 3            // 0-based month after quarter end
  return isoDate(new Date(q.year, firstMonthAfter + 1, 0))
}

export function daysBetween(fromIso: string, toIso: string): number {
  const a = new Date(fromIso + 'T00:00:00').getTime()
  const b = new Date(toIso + 'T00:00:00').getTime()
  return Math.round((b - a) / 86_400_000)
}

/** Signed total of cash and bank accounts (Dr +, Cr -). */
export function cashAndBank(balances: LedgerBalance[]): { total: string; accounts: number } {
  const rows = balances.filter((b) => b.cash_bank)
  return {
    total: addMoney('0', ...rows.map((b) => signed(b.balance, b.side))),
    accounts: rows.length,
  }
}

/** Outstanding that is past its due date: everything but the "current" bucket. */
export function pastDue(r: OutstandingReport | undefined): string {
  if (!r) return '0.00'
  return addMoney('0', ...BUCKETS.filter((b) => b !== 'current').map((b) => r.totals[b] ?? '0'))
}

export type Tone = 'danger' | 'warning' | 'info'

export interface Attention {
  tone: Tone
  /** i18n key and its values. */
  key: string
  values: Record<string, string | number>
  /** Where the user fixes it. */
  path: string
}

/**
 * What the owner should act on, most urgent first. Built only from what
 * the app already knows; nothing is guessed.
 */
export function needsAttention({ receivable, payable, balances, vat, today }: {
  receivable?: OutstandingReport
  payable?: OutstandingReport
  balances?: LedgerBalance[]
  vat?: { quarter: Quarter; due: string; payable?: string }
  today: string
}): Attention[] {
  const out: Attention[] = []

  // A cash or bank account in credit is either an overdraft or an entry
  // posted to the wrong side. Either way someone should look.
  for (const b of balances ?? []) {
    if (b.cash_bank && b.side === 'C') {
      out.push({ tone: 'danger', key: 'dashboard.attn.cashInCredit',
                 values: { name: b.name_en ?? b.code, nameAr: b.name_ar ?? '', amount: b.balance },
                 path: `/ledger?code=${encodeURIComponent(b.code)}` })
    }
  }

  if (receivable) {
    const late = receivable.parties.filter((p) => isPositive(p.buckets.d90_plus ?? '0'))
    if (late.length) {
      out.push({ tone: 'danger', key: 'dashboard.attn.overdue90',
                 values: { count: late.length, amount: receivable.totals.d90_plus },
                 path: '/outstanding' })
    }
  }

  if (payable && isPositive(pastDue(payable))) {
    out.push({ tone: 'warning', key: 'dashboard.attn.payablesPastDue',
               values: { amount: pastDue(payable) }, path: '/outstanding' })
  }

  if (vat) {
    const left = daysBetween(today, vat.due)
    if (left >= 0 && left <= 45) {
      out.push({ tone: left <= 10 ? 'danger' : 'warning', key: 'dashboard.attn.vatDue',
                 values: { quarter: vat.quarter.quarter, year: vat.quarter.year,
                           due: vat.due, days: left, amount: vat.payable ?? '' },
                 path: '/vat-return' })
    }
  }

  const rank: Record<Tone, number> = { danger: 0, warning: 1, info: 2 }
  return out.sort((a, b) => rank[a.tone] - rank[b.tone])
}

/** 1,126,400 -> "1.1M", 84,000 -> "84K": for chart axes only. */
export function compact(n: number): string {
  const a = Math.abs(n)
  if (a >= 1_000_000) return `${(n / 1_000_000).toFixed(a >= 10_000_000 ? 0 : 1)}M`
  if (a >= 1_000) return `${Math.round(n / 1_000)}K`
  return String(Math.round(n))
}

/** Income and expenses of one P&L statement, as positive strings. */
export function pnlTotals(pl: {
  direct_income: { amount: string }[]; indirect_income: { amount: string }[]
  direct_expenses: { amount: string }[]; indirect_expenses: { amount: string }[]
}): { income: string; expenses: string } {
  const sum = (xs: { amount: string }[]) => addMoney('0', ...xs.map((x) => x.amount))
  return {
    income: addMoney(sum(pl.direct_income), sum(pl.indirect_income)),
    expenses: addMoney(sum(pl.direct_expenses), sum(pl.indirect_expenses)),
  }
}

export { subMoney }
